import elementsInView from '@helpers/elementsInView';

type Module<T extends HTMLElement> = { default: (elements: T[]) => void };

type Importer<T extends HTMLElement> = () => Promise<Module<T>>;

type ImporterMap<T extends HTMLElement[]> = {
  [K in keyof T]: {
    selector: string;
    importer: Importer<T[K]>;
    distance?: number;
    dynamic?: boolean;
  };
};

async function getElements<T extends Element>(selector: string, dynamic: boolean) {
  if (dynamic && document.querySelector('.dynamic-block')) {
    await new Promise<void>((resolve) => {
      window.addEventListener(
        'servd.dynamicloaded',
        () => {
          resolve();
        },
        { once: true },
      );
    });
  }

  return [...document.querySelectorAll<T>(selector)];
}

export async function loadModule<T extends HTMLElement>(
  selector: string,
  importer: Importer<T>,
  dynamic = false,
) {
  const elements = await getElements<T>(selector, dynamic);
  if (!elements.length) return;

  const module = await importer();
  module.default(elements);
}

export async function loadModuleWhenInView<T extends HTMLElement>(
  selector: string,
  importer: Importer<T>,
  distance = 0,
  dynamic = false,
) {
  const elements = await getElements<T>(selector, dynamic);
  if (!elements.length) return;

  await elementsInView(elements, distance);
  const module = await importer();
  module.default(elements);
}

export async function loadModulesWhenInView<T extends HTMLElement[]>(importers: ImporterMap<T>) {
  return Promise.all([
    ...importers.map(({ selector, importer, distance, dynamic }) =>
      loadModuleWhenInView(selector, importer, distance, dynamic),
    ),
  ]);
}
